import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  Link as MuiLink,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  Paper,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  ListItemIcon,
} from "@mui/material";
import { faMicrochipAi } from "@fortawesome/pro-solid-svg-icons";
import SetDocumentMeta from "../common/SetDocumentMeta";
import EmbededContactForm from "./EmbededContactForm";
import * as CONFIG from "./utils/kthGlobalLabelsVars";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AiServiceCategories from "./ai-service-components/AiServiceCategories";
import AiPatentAnalysis from "./ai-service-components/AiPatentAnalysis";
import AiEvaluation from "./ai-service-components/AiEvaluation";
import AiAgreementsSummary from "./ai-service-components/AiAgreementsSummary";
import AiDataAnnotation from "./ai-service-components/AiDataAnnotation";
import AiTechnologyTransactions from "./ai-service-components/AiTechnologyTransactions";
import AiLitigation from "./ai-service-components/AiLitigation";
import AiCompliance from "./ai-service-components/AiCompliance";
import AiWhyChooseUs from "./ai-service-components/AiWhyChooseUs";
import { getImageUrl } from "../../utils/helpers";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GavelIcon from "@mui/icons-material/Gavel";
import DataObjectIcon from "@mui/icons-material/DataObject";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import SecurityIcon from "@mui/icons-material/Security";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import DescriptionIcon from "@mui/icons-material/Description";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MemoryIcon from "@mui/icons-material/Memory";
import RouterIcon from "@mui/icons-material/Router";
import BalanceIcon from "@mui/icons-material/Balance";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LaunchIcon from "@mui/icons-material/Launch";
import KeyIcon from "@mui/icons-material/Key";
import CopyrightIcon from "@mui/icons-material/Copyright";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import LanguageIcon from "@mui/icons-material/Language";
import InfoBox from "./utils/InfoBox";

// Define "Jump to Section" items with icons
const jumpToSections = [
  {
    id: "ai-agreements",
    label: "AI Agreements & Legal Terms",
    icon: <GavelIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-legal-evaluation",
    label: "AI Application Evaluation",
    icon: <AssessmentIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-patent-analysis",
    label: "AI in Patent Analysis",
    icon: <SmartToyIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-tech-transactions",
    label: "AI Technology Transactions",
    icon: <SwapHorizontalCircleIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-compliance",
    label: "AI Regulatory Compliance",
    icon: <SecurityIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-legal-annotation",
    label: "AI Data Annotation",
    icon: <DataObjectIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },

  {
    id: "ai-litigation",
    label: "AI in Litigation",
    icon: <BalanceIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
];

const patentResourceLinks = [
  {
    id: "news-insights",
    label: "Patent News & Insights",
    icon: <DescriptionIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/news-insights?practice=patents",
  },
  {
    id: "patent-ai-resources",
    label: "Patent AI Resources",
    icon: <SmartToyIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/patent-ai-resources",
  },
  {
    id: "ai-agreements",
    label: "Legal Agreements for AI Technology",
    icon: <LanguageIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/legal-agreements-for-ai",
  },
  {
    id: "trademark",
    label: "Brand Protection (Trademarks)",
    icon: <BrandingWatermarkIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/trademarks",
  },
  {
    id: "copyright",
    label: "Software Licensing (Copyrights)",
    icon: <CopyrightIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/software-licensing",
  },
  {
    id: "trade-secret",
    label: "Trade Secret Protection",
    icon: <KeyIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/trade-secrets",
  },
  {
    id: "contact-section",
    label: "Schedule a Consultation",
    icon: <PermContactCalendarIcon sx={{ mr: 1, color: "#007BFF" }} />,
  },
];

const scrollToElement = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const KthServicesAi = ({ baseConfig }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      scrollToElement(hash);
    }
  }, []);

  // Custom styles
  const sectionStyles = {
    header: {
      py: 3,
      px: isMobile ? 2 : 4,
      mb: 4,
      borderRadius: 2,
      backgroundImage: `linear-gradient(135deg, ${tertColor}22 0%, ${tertColor}44 100%)`,
      boxShadow: "0 3px 5px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      backgroundColor: tertColor,
      color: "white",
      fontWeight: "medium",
      display: "flex",
      alignItems: "center",
    },
    card: {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: 2,
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transform: "translateY(-2px)",
      },
    },
  };

  SetDocumentMeta({
    title: "AI Technology Lawyer | Evaluation, Compliance & Data Annotation",
    description:
      "Ensure your AI systems are accurate, compliant, and optimized for legal analysis. Our expert legal team provides AI evaluation, training data labeling, and annotation services tailored to your needs. Specializing in AI patent tools.",
    keywords:
      "AI legal evaluation, AI training data labeling, AI annotation, legal AI services, AI compliance, AI in intellectual property law, AI for patent analysis",
  });

  return (
    <Container sx={{ py: 4 }} className="link-no-decoration">
      {/* ======== PAGE HEADER ======== */}
      <Box component="header" sx={sectionStyles.header}>
        {/* Banner / Headline */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            color="text.primary"
          >
            Legal Counseling for AI & ML
          </Typography>
          <Typography
            variant="h6"
            fontWeight="medium"
            color="text.secondary"
            gutterBottom
          >
            Expert Attorney Services for Artificial Intelligence and Machine
            Learning
          </Typography>
          <Divider
            sx={{ my: 2, width: "20%", mx: "auto", borderColor: tertColor }}
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            lineHeight: 1.8,
            textAlign: "center",
            maxWidth: "85%",
            mx: "auto",
            fontSize: "1.1rem",
          }}
        >
          <strong> In today's rapidly evolving AI landscape,</strong> navigating
          the complex legal, regulatory, and intellectual property
          considerations is critical to success. Our firm provides comprehensive
          legal services tailored to AI technologies, from evaluation and
          compliance to contracts and data annotation, combining technical
          expertise with legal knowledge to help you innovate.
        </Typography>
      </Box>

      {/* ======== TABLE OF CONTENTS ======== */}
      <Card variant="outlined" sx={{ ...sectionStyles.card, mb: 4 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="medium"
            sx={{
              color: tertColor,
              borderBottom: `2px solid ${tertColor}`,
              pb: 1,
            }}
          >
            Quick Navigation
          </Typography>

          <Grid container spacing={3}>
            {/* Main Patent Services */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ pt: 1 }}
              >
                Patent Services
              </Typography>
              <List dense disablePadding>
                {jumpToSections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      onClick={() => scrollToSection(section.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: `${tertColor}22`,
                          pl: 2,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText primary={section.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Patent Resources */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ pt: 1 }}
              >
                Patent Resources & Related Services
              </Typography>
              <List dense disablePadding>
                {patentResourceLinks.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    {section.externalLink ? (
                      <ListItemButton
                        component={RouterLink}
                        to={section.externalLink}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: `${linkColor}22`,
                            pl: 2,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText primary={section.label} />
                        <LaunchIcon
                          sx={{ fontSize: 16, ml: 1, color: linkColor }}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: `${linkColor}22`,
                            pl: 2,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText primary={section.label} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Fixed Fee AI Counseling Info Box */}
      <InfoBox
        title="SPECIAL OFFER: Discounted Fixed Fee AI Agreement Drafting"
        content={
          <Box>
            <Typography variant="body1" paragraph>
              We provide discounted fixed fee drafting services for AI
              application agreements. Whether you need AI SaaS agreements, model
              licensing terms, or data sharing contracts, we can provide you
              with carefully drafted documents at a predictable cost.
            </Typography>
            <Box display="flex" mt={2}>
              <Button
                startIcon={<PhoneInTalkIcon />}
                variant="contained"
                onClick={() => scrollToSection("contact-section")}
                sx={{
                  backgroundColor: linkColor,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: priHoverColor,
                  },
                }}
              >
                Request Free Quote & Strategy Consultation
              </Button>
            </Box>
          </Box>
        }
        type="primary"
        icon={<GavelIcon />}
      />

      {/* Service Categories Info Box */}
      <Box id="ai-service-categories" sx={{ mt: 4 }}>
        <AiServiceCategories baseConfig={baseConfig} />
      </Box>

      {/* AI Agreements Summary Section */}
      <Box id="ai-agreements">
        <AiAgreementsSummary baseConfig={baseConfig} />
      </Box>

      {/* AI Legal Evaluation Section */}
      <Box id="ai-legal-evaluation">
        <AiEvaluation baseConfig={baseConfig} />
      </Box>

      {/* AI Patent Analysis Section */}
      <Box id="ai-patent-analysis">
        <AiPatentAnalysis baseConfig={baseConfig} />
      </Box>

      {/* AI Technology Transactions Section */}
      <Box id="ai-tech-transactions">
        <AiTechnologyTransactions baseConfig={baseConfig} />
      </Box>

      {/* AI Compliance Section */}
      <Box id="ai-compliance">
        <AiCompliance baseConfig={baseConfig} />
      </Box>

      {/* AI Data Annotation and Prompt Engineering Section */}
      <Box id="ai-legal-annotation">
        <AiDataAnnotation baseConfig={baseConfig} />
      </Box>

      {/* AI in Litigation Section */}
      <Box id="ai-litigation">
        <AiLitigation baseConfig={baseConfig} />
      </Box>

      {/* Why Choose Us Section */}
      <Box id="why-choose-us">
        <AiWhyChooseUs baseConfig={baseConfig} />
      </Box>

      {/* ======== CONTACT / CTA SECTION ======== */}
      <Box
        component="section"
        id="contact-section"
        sx={{
          mt: 0,
          p: 4,
          borderRadius: 2,
          backgroundColor: "#f3f6f9",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${tertColor}33`,
        }}
      >
        {/* Title spanning both columns */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color={tertColor}>
            Ready to Discuss Your AI Legal Needs?
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            For Immediate Assistance, Call Us At:{" "}
            <Chip
              label="+1-602-837-0631"
              color="primary"
              sx={{
                backgroundColor: tertColor,
                fontWeight: "bold",
                fontSize: "1rem",
                height: "32px",
                "& .MuiChip-label": {
                  color: "white",
                },
              }}
            />
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT SIDE: Image + Overview */}
          <Grid item xs={12} md={6}>
            <Box
              display="flex"
              justifyContent="center"
              mb={3}
              sx={{
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 2,
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
                  pointerEvents: "none",
                },
              }}
            >
              <img
                src={getImageUrl("kth-head-2-ai.png")}
                alt="AI Law Consultation Contact Form"
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Box>

            <Paper
              elevation={0}
              sx={{ p: 2, backgroundColor: "white", borderRadius: 2 }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: tertColor }}
              >
                What to Expect During the Free Consultation:
              </Typography>

              <List>
                {[
                  "We will assess your specific AI legal requirements and gather necessary information for a conflict of interest check.",
                  "We will provide you with a preliminary cost estimate and projected timeline for the required legal services.",
                ].map((text, index) => (
                  <ListItem key={index} sx={{ py: 0.75 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: tertColor, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={tertColor}
                  >
                    Evaluation of AI Applications
                  </Typography>
                  <List dense>
                    {[
                      "Brief review of your AI system architecture and implementation",
                      "Briefly discuss IP considerations and potential gaps and risks",
                    ].map((text, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ChevronRightIcon
                          sx={{ fontSize: 16, color: tertColor, mr: 1 }}
                        />
                        <ListItemText primary={text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={tertColor}
                  >
                    Legal Strategy Development
                  </Typography>
                  <List dense>
                    {[
                      "Recommendations for contractual framework and agreements",
                      "Strategic approaches to protect your AI intellectual property",
                    ].map((text, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ChevronRightIcon
                          sx={{ fontSize: 16, color: tertColor, mr: 1 }}
                        />
                        <ListItemText primary={text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT SIDE: Reusable Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: tertColor, color: "white" }}>
                <Typography variant="h5" fontWeight="medium" align="center">
                  Book Your Complimentary AI Legal Consultation
                </Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                  Please complete the form below, and our team will reach out to
                  schedule a free phone consultation with one of our experienced
                  AI attorneys at your earliest convenience.
                </Typography>

                <EmbededContactForm
                  dropDownOptions={[
                    "AI Application Evaluation",
                    "AI Legal Agreements",
                    "Patent AI Services",
                    "AI Data Annotation",
                    "AI Compliance Issues",
                    "Technology Transactions",
                    "Other AI Legal Matter",
                  ]}
                  submitButtonLabel="Schedule My Free Phone Consultation"
                  disclaimerHTML="Please note that submitting this form does not create an attorney-client relationship. Any information shared will be kept confidential."
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ======== FINAL DISCLAIMER ======== */}
      <Box
        textAlign="center"
        mt={6}
        pt={2}
        borderTop="1px solid #ccc"
        sx={{
          backgroundColor: "#f9f9f9",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          Disclaimer: Please do not share confidential details until we confirm
          our representation.
        </Typography>
      </Box>
    </Container>
  );
};

export default KthServicesAi;
